import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import Drawer from "react-modern-drawer";
import {
  useOrderServicePostOrderOrderlines,
  useProjectFolderServiceGetProjectFolder1,
  useProjectFolderServiceGetProjectFolderAllorders,
  useProjectFolderServiceGetProjectFolderAllordersKey,
  useProjectFolderServicePutProjectFolderDone,
} from "../../openapi/queries";
import { OrderlineViewDTO } from "../../openapi/requests";
import "../layout/elements/Lists.scss";
import "./ProjectDetail.scss";
import { Option } from "../../types/types";
import { OrderlineListV2 } from "./OrderlineListV2";
import Select, { StylesConfig } from "react-select";
import IssueReporting from "../layout/IssueReporting";
import OrderlinesUploadToDam from "./OrderlinesUploadToDam";

interface Props {
  projectFolderId?: number;
  isOpen: boolean;
  onClose: () => void;
}

export const ProjectFolderDetail: React.FC<Props> = ({ projectFolderId, isOpen, onClose }) => {
  const [files, setFiles] = useState<OrderlineViewDTO[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<OrderlineViewDTO[]>([]);
  const [selectedProjectFileFilter, setSelectedProjectFileFilter] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [showOrderlineUpload, setShowOrderlineUpload] = useState<boolean>(false);

  const { data: projectFolder } = useProjectFolderServiceGetProjectFolder1({ key: projectFolderId ?? 0 }, undefined, {
    enabled: isOpen && projectFolderId !== undefined, refetchOnWindowFocus: false
  });

  const { mutateAsync: markDone } = useProjectFolderServicePutProjectFolderDone();

  const queryClient = useQueryClient();

  const {
    data: orders,   
    isFetching: isOrdersFetching,
  } = useProjectFolderServiceGetProjectFolderAllorders({ key: projectFolderId ?? 0 }, [useProjectFolderServiceGetProjectFolderAllordersKey, projectFolderId], {
    enabled: isOpen,
    refetchOnWindowFocus: false,
  });

  const { mutateAsync: getOrderlines } = useOrderServicePostOrderOrderlines();

  useEffect(() => {
    setLoading(true);
    if (orders && !isOrdersFetching) {
      async function fetchData() {
        const orderlines = await getOrderlines({ requestBody: orders.map((x) => x.id) });
        var files: OrderlineViewDTO[] = [];

        if (orderlines) {
          for (let j = 0; j < orderlines.length; j++) {
            const line = orderlines[j];

            if (line.files.length) {
              files.push(line);
            }
          }
        }

        if (files.length > 0) {  
          setLoading(false);
          setFiles(files);          
        }
      }
      fetchData();
      
    }
  }, [getOrderlines, isOrdersFetching, orders]);

  const onProjectFileFilterChange = async (e: any) => {
    setSelectedProjectFileFilter(e.value);
    queryClient.invalidateQueries({ queryKey: [useProjectFolderServiceGetProjectFolderAllordersKey] });
  };

  const options: Array<Option> = [
    { value: "", label: "Any status" },
    { value: "latest", label: "Latest" },
    { value: "preview", label: "Preview" },
    { value: "adjustment", label: "In adjustment" },
    { value: "adjustment_complete", label: "Adjustment complete" },
    { value: "final", label: "Final render" },
    { value: "approved", label: "Approved" },    
    { value: "dam", label: "On Dam" },
  ];

  const colourStyles: StylesConfig<Option, true> = {
    control: (styles) => ({
      ...styles,
      borderRadius: "0",
      fontSize: "12px",
      minHeight: "32px",
      minWidth: "180px",
      backgroundColor: "#1A1A1A",
    }),
    singleValue: (styles) => ({
      ...styles,
      color: "#FFF",
    }),
    menu: (styles) => ({
      ...styles,
      padding: "0px",
      margin: "0px",
      backgroundColor: "#1A1A1A",
      borderRadius: "0",
      borderStyle: "solid",
      borderWidth: "1px",
      borderColor: "#FFF",
    }),
    indicatorSeparator: (styles) => ({
      ...styles,
      display: "none",
      backgroundColor: "#1A1A1A",
      color: "#1A1A1A",
    }),
    dropdownIndicator: (styles) => ({
      ...styles,
      padding: "0px 8px 0px 0px",
      color: "#FFF",
    }),
    clearIndicator: (styles) => ({
      ...styles,
      padding: "0px 0px 0px 0px",
      display: "none",
    }),
    noOptionsMessage: (styles) => ({
      ...styles,
      fontSize: "12px",
      backgroundColor: "#1A1A1A",
      color: "#FFF",
    }),
    option: (styles, { data, isDisabled, isFocused, isSelected }) => ({
      ...styles,
      display: "flex",
      alignItems: "center",
      fontSize: "12px",
      color: isSelected ? "#1A1A1A" : isFocused ? "#1A1A1A" : "#FFF",
      backgroundColor: isSelected ? "#FFF" : isFocused ? "#FFF" : "#1A1A1A",
      ":active": {
        ...styles[":active"],
        color: isSelected ? "#1A1A1A" : isFocused ? "#1A1A1A" : "#FFF",
        backgroundColor: isSelected ? "#FFF" : isFocused ? "#FFF" : "#1A1A1A",
      },
    }),
  };

  const onUpdated = async () => {
    setFiles([]);
    queryClient.invalidateQueries({ queryKey: [useProjectFolderServiceGetProjectFolderAllordersKey] });
  };

  const onMarkDone = async () => {
    if (projectFolderId) {
      await markDone({ key: projectFolderId });
    }
  };

  const onMoveToDAM = (orderlines: OrderlineViewDTO[]) => {
    setSelectedFiles(orderlines);
    setShowOrderlineUpload(true);
  };

  return (
    <Drawer className="project-detail-overlay" enableOverlay={false} open={isOpen} size={"100%"} direction="bottom" onClose={onClose}>
      <div className="project-detail-overlay-header">
        <button onClick={onClose}>
          <svg width="56" height="28" viewBox="0 0 56 28" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="0.5" y="0.5" width="55" height="27" rx="13.5" fill="#1A1A1A" stroke="white" />
            <path d="M30.5 10.5L23.5 17.5" stroke="white" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M23.5 10.5L30.5 17.5" stroke="white" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
      <div className="project-detail-overlay-content">
        <div className="project-detail">
          <div className="project-detail-header">
            <h1>{projectFolder?.name}</h1>
          </div>

          <div className="project-content-wrapper full">
            {/* <div>
              <div className="info-list">
                <div className="info-list-header">
                  <span>Basics</span>
                </div>
                <ul>
                  <li>
                    <span>Name</span>
                    <span>{projectFolder?.name}</span>
                  </li>
                  <li>
                    <span>Order By</span>
                    <span>{projectFolder?.modifiedUserName}</span>
                  </li>
                  <li>
                    <span>Order Time</span>
                    <span>{format(new Date(projectFolder?.timestampCreate ?? "1900-01-01"), "dd.MM.yyyy - pp")}</span>
                  </li>
                  <li>
                    <span>Last Status Update</span>
                    <span>{format(lastUpdate, "dd.MM.yyyy - pp")}</span>
                  </li>
                  <li>
                    <span>Files Ordered</span>
                    <span>{projectFilesOrdered}</span>
                  </li>
                  <li>
                    <span>Files Produced</span>
                    <span>{projectFilesProduced}</span>
                  </li>
                </ul>
              </div>
            </div> */}

            <div className="info-list project-images-list">
              <div className="info-list-header">
                <span>Images created with this scene</span>
                {/* <span>
                  <button
                    className="btn"
                    onClick={() => {
                      onDownloadAll(projectFolderId ?? 0);
                    }}
                  >
                    Download all
                  </button>
                  <button className="btn" onClick={onDownloadPowerpoint}>
                    Download as powerpoint
                  </button>
                </span> */}
                <div className="project-images-list-topactions">
                  <span>
                    <Select options={options} placeholder="" onChange={onProjectFileFilterChange} defaultValue={options[0]} styles={colourStyles} />
                  </span>
                  <span>
                    <IssueReporting type="project" projectId={projectFolderId} className="link" />
                    <button className="rounded" onClick={onMarkDone}>
                      Mark project as done
                    </button>
                  </span>
                </div>
              </div>
              {/* {loading && (
                <div className="loading">
                  <Loading />
                </div>
              )} */}
              <OrderlineListV2
                project={projectFolder}
                   orders={orders}
                orderlines={files}             
                filter={options.find((x) => x.value === selectedProjectFileFilter)}
                loading={loading}
                view="grid"
                onUpdate={() => onUpdated()}
                onMoveToDAM={onMoveToDAM}
              />
              <OrderlinesUploadToDam isOpen={showOrderlineUpload} onClose={() => setShowOrderlineUpload(false)} projectOrFolder={projectFolder} orderlines={selectedFiles} />
              {/* <OrderlineList orderlines={files} /> */}
              {/* {files?.length === 0 && !loading && (
                <div className="project-images-list-no-images">
                  <span>No available images</span>
                </div>
              )} */}
            </div>
          </div>
        </div>
      </div>
    </Drawer>
  );
};
